/**
 * Mark a skill sheet for one person in the class (M44).
 *
 * Brian, 22 Sep: "on the instructor's roster click a participant, pick the
 * skill, mark down the row; the completed sheet becomes a file in that
 * person's record." Pick the sheet (narrowed to the person's level), the
 * device if the sheet has device blocks, then S / U down the rows. N/A is
 * offered only on a step tiered above the person's level. The result is the
 * checksheet forms' own rule, shown live, and saving is final — a redo is a
 * new attempt, never an edit.
 */
import { useMemo, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from './ErrorBanner.jsx';

const LEVELS = ['EMR', 'EMT', 'AEMT', 'PARAMEDIC'];
/** The person's level from what they called themselves at check-in. */
export function levelOf(roleTitle) {
  const t = String(roleTitle ?? '').toUpperCase();
  if (/PARAMEDIC|MEDIC\b|EMT-P\b/.test(t)) return 'Paramedic';
  if (/\bAEMT\b|ADVANCED EMT/.test(t)) return 'AEMT';
  if (/\bEMT\b|EMT-B/.test(t)) return 'EMT';
  if (/\bEMR\b|FIRST RESPONDER/.test(t)) return 'EMR';
  return null;
}
const rank = (l) => LEVELS.indexOf(String(l ?? '').toUpperCase());

function rowsFor(template, device) {
  if (!template) return [];
  const core = template.steps.map((s) => ({ ...s, k: `A${s.n}`, part: 'core' }));
  if (!template.device_blocks) return core;
  if (!device) return null;
  return [...core, ...(template.device_blocks[device] ?? []).map((s) => ({ ...s, k: `${device}:${s.n}`, part: device }))];
}

export function liveResult(rows, ratings) {
  if (!rows) return null;
  let anyU = false;
  let critU = false;
  let unmarked = 0;
  for (const r of rows) {
    if (r.header) continue;
    const v = ratings[r.k];
    if (!v) unmarked += 1;
    if (v === 'U') {
      anyU = true;
      if (r.critical) critU = true;
    }
  }
  return { unmarked, result: critU ? 'FAIL_CRITICAL' : anyU ? 'REMEDIATE' : 'PASS' };
}

const WORDS = { PASS: 'Pass', REMEDIATE: 'Remediate', FAIL_CRITICAL: 'Fail — critical step' };

export function SkillSheetMarker({ sessionId, person, templates, onSaved, onCancel }) {
  const personLevel = levelOf(person.role_title);
  const [level, setLevel] = useState(personLevel ?? '');
  const [showAll, setShowAll] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [device, setDevice] = useState(null);
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const choices = useMemo(
    () =>
      (templates ?? []).filter(
        (t) => showAll || !level || !t.cert_levels?.length || t.cert_levels.some((l) => l.toUpperCase() === level.toUpperCase())
      ),
    [templates, level, showAll]
  );
  const template = (templates ?? []).find((t) => t.id === templateId) ?? null;
  const rows = rowsFor(template, device);
  const live = liveResult(rows, ratings);

  function pick(id) {
    setTemplateId(id);
    setDevice(null);
    setRatings({});
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/v1/sessions/${sessionId}/skill-sheets`, {
        method: 'POST',
        body: JSON.stringify({
          token: person.token,
          template_id: template.id,
          device,
          cert_level: level || null,
          ratings: Object.entries(ratings).map(([k, rating]) => ({ k, rating })),
          notes: notes.trim() || null,
        }),
      });
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="marker-heading">
      <h2 id="marker-heading">Skill sheet — {person.full_name}</h2>
      <ErrorBanner error={error} />
      <div className="field-row">
        <label className="field">
          <span>Their level</span>
          <select value={level} onChange={(e) => setLevel(e.target.value)} disabled={busy}>
            <option value="">Not stated</option>
            <option value="EMR">EMR</option>
            <option value="EMT">EMT</option>
            <option value="AEMT">AEMT</option>
            <option value="Paramedic">Paramedic</option>
          </select>
        </label>
        <label className="field">
          <span>Skill sheet</span>
          <select value={templateId} onChange={(e) => pick(e.target.value)} disabled={busy}>
            <option value="">Choose a sheet…</option>
            {choices.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
                {t.ref ? ` (${t.ref})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="checkbox">
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} disabled={busy} />
        <span>Show sheets for every level</span>
      </label>
      {(templates ?? []).length === 0 && <p className="muted">The library is empty — a coordinator imports the checksheets under Skill sheets.</p>}

      {template?.level_text && (
        <p className="muted">
          <strong>Level:</strong> {template.level_text}
        </p>
      )}
      {template?.device_blocks && (
        <p className="toolbar">
          <span className="muted">Device used:</span>
          {Object.keys(template.device_blocks).map((d) => (
            <button
              key={d}
              type="button"
              className={`btn btn--auto ${device === d ? 'btn--primary' : ''}`}
              onClick={() => {
                setDevice(d);
                setRatings({});
              }}
              disabled={busy}
            >
              {d}
            </button>
          ))}
        </p>
      )}

      {rows && rows.length > 0 && (
        <>
          <div className="schedule__scroll">
            <table className="schedule__table skill-sheet">
              <thead>
                <tr>
                  <th scope="col">Procedure step (◆ critical)</th>
                  <th scope="col">S</th>
                  <th scope="col">U</th>
                  <th scope="col">N/A</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  if (r.header) {
                    return (
                      <tr key={r.k}>
                        <td colSpan={4}>
                          <strong>{r.text}</strong>
                        </td>
                      </tr>
                    );
                  }
                  const naOk = Boolean(r.tier) && (rank(level) < 0 || rank(r.tier) > rank(level));
                  const firstOfPart = r.part !== 'core' && rows[i - 1]?.part !== r.part;
                  return [
                    firstOfPart ? (
                      <tr key={`${r.k}-part`}>
                        <td colSpan={4}>
                          <strong>Device block: {r.part}</strong>
                        </td>
                      </tr>
                    ) : null,
                    <tr key={r.k} className={ratings[r.k] === 'U' ? 'row--awaiting' : undefined}>
                      <td>
                        {r.critical ? <strong>◆ </strong> : null}
                        {r.tier ? <span className="badge">{r.tier}</span> : null} {r.n}. {r.text}
                        {r.note ? <div className="muted">{r.note}</div> : null}
                      </td>
                      {['S', 'U', 'NA'].map((v) => (
                        <td key={v} style={{ textAlign: 'center' }}>
                          {v === 'NA' && !naOk ? null : (
                            <input
                              type="radio"
                              name={`rate-${r.k}`}
                              aria-label={`Step ${r.n} ${v === 'NA' ? 'not applicable' : v}`}
                              checked={ratings[r.k] === v}
                              onChange={() => setRatings((x) => ({ ...x, [r.k]: v }))}
                              disabled={busy}
                            />
                          )}
                        </td>
                      ))}
                    </tr>,
                  ];
                })}
              </tbody>
            </table>
          </div>
          <label className="field">
            <span>Evaluator comments (optional)</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} disabled={busy} />
          </label>
          <p className="toolbar">
            <button type="button" className="btn btn--primary btn--auto" disabled={busy || !live || live.unmarked > 0} onClick={save}>
              {busy ? 'Saving…' : live?.unmarked ? `${live.unmarked} step${live.unmarked === 1 ? '' : 's'} still to mark` : `Save — ${WORDS[live.result]}`}
            </button>
            <button type="button" className="btn btn--quiet btn--auto" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <span className="muted">Saving is final: a completed sheet cannot be changed. Another try is a new attempt.</span>
          </p>
        </>
      )}
      {!templateId && (
        <p className="toolbar">
          <button type="button" className="btn btn--quiet btn--auto" onClick={onCancel}>
            Cancel
          </button>
        </p>
      )}
    </section>
  );
}

export default SkillSheetMarker;
